import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  fetchSupportQueue,
  postAssignTask,
  postResolveTicket,
  postSupportResponse,
  type ConversationMessage,
  type FetchSupportQueueParams,
  type SupportQueuePagination,
  type SupportTicket,
  type SupportTicketMessage,
} from "../lib/api";
import { useDocumentTitle } from "../hooks/use-document-title";
import { useConversationSocket } from "../hooks/use-conversation-socket";
import { getUserInfo } from "../lib/auth";

const PAGE_SIZE = 25;
const TEAM_FILTER_STORAGE_KEY = "otto.support-queue.team";
const STATUS_FILTER_STORAGE_KEY = "otto.support-queue.show-resolved";

const TEAM_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "All teams" },
  { value: "customer_service", label: "Customer Service" },
  { value: "bidding_support", label: "Bidding Support" },
  { value: "transport", label: "Transport" },
  { value: "general", label: "General" },
];

function priorityBadge(priority: string): string {
  switch (priority) {
    case "high":
      return "badge badge-error";
    case "medium":
      return "badge badge-warning";
    default:
      return "badge badge-neutral";
  }
}

function statusBadge(status: string): string {
  switch (status) {
    case "open":
      return "badge badge-warning";
    case "assigned":
      return "badge badge-info";
    case "resolved":
      return "badge badge-success";
    case "closed_by_dealer":
    case "closed":
      return "badge badge-neutral";
    default:
      return "badge badge-neutral";
  }
}

function statusLabel(status: string): string {
  return status === "closed_by_dealer" ? "closed by dealer" : status;
}

function titleCase(str: string): string {
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

function dealerDisplay(ticket: SupportTicket): { primary: string; secondary: string } {
  const name = `${ticket.dealerFirstName ?? ""} ${ticket.dealerLastName ?? ""}`.trim();
  const org = (ticket.dealerOrgName || ticket.dealerDealership || "").trim();
  const primary = name ? titleCase(name) : `Dealer #${ticket.dealerId}`;
  const secondary = org ? titleCase(org) : "";
  return { primary, secondary };
}

function readStoredString(key: string): string {
  try {
    return window.localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function writeStoredString(key: string, value: string): void {
  try {
    if (value) {
      window.localStorage.setItem(key, value);
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    /* localStorage unavailable (private mode, quota) — filter still works in-session */
  }
}

function MessageRow({ message }: { message: SupportTicketMessage }) {
  const isInbound = message.direction === "inbound";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: isInbound ? "flex-start" : "flex-end",
      }}
    >
      <div
        style={{
          maxWidth: "85%",
          padding: "8px 12px",
          borderRadius: 10,
          background: isInbound ? "var(--color-surface-solid)" : "rgba(53, 211, 170, 0.12)",
          border: isInbound
            ? "1px solid var(--color-border)"
            : "1px solid rgba(53, 211, 170, 0.4)",
          color: "var(--color-text)",
          fontSize: 13,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {message.content || <span style={{ opacity: 0.6 }}>(no content)</span>}
      </div>
      <div
        style={{
          marginTop: 2,
          fontSize: 11,
          color: "var(--color-text-soft)",
          textAlign: isInbound ? "left" : "right",
        }}
      >
        {isInbound ? "Dealer" : message.sentBy ? `Sent by: ${message.sentBy}` : "Otto"} ·{" "}
        {new Date(message.timestamp).toLocaleString()}
      </div>
    </div>
  );
}

const AUTO_SCROLL_STICKY_THRESHOLD_PX = 24;

function LiveConversationContext({
  ticket,
}: {
  ticket: SupportTicket;
}) {
  const [messages, setMessages] = useState<SupportTicketMessage[]>(ticket.recentMessages ?? []);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickyToBottomRef = useRef(true);

  useEffect(() => {
    setMessages(ticket.recentMessages ?? []);
    stickyToBottomRef.current = true;
  }, [ticket.ticketId, ticket.recentMessages]);

  const handleSocketMessage = useCallback((incoming: ConversationMessage) => {
    setMessages((prev) => {
      if (prev.some((m) => m.messageId === incoming.messageId)) {
        return prev;
      }
      const asTicketMessage: SupportTicketMessage = {
        messageId: incoming.messageId,
        direction: incoming.direction as "inbound" | "outbound",
        content: incoming.content,
        timestamp: incoming.timestamp,
        messageType: incoming.messageType,
        sentBy: typeof incoming.metadata?.sentBy === "string" ? incoming.metadata.sentBy : null,
      };
      return [...prev, asTicketMessage];
    });
  }, []);

  const { status } = useConversationSocket({
    dealerId: String(ticket.dealerId),
    onMessage: handleSocketMessage,
  });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (stickyToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickyToBottomRef.current = distanceFromBottom <= AUTO_SCROLL_STICKY_THRESHOLD_PX;
  }, []);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <div className="section-title" style={{ margin: 0 }}>Conversation Context</div>
        <span
          className={`badge ${
            status === "connected" ? "badge-success" : status === "connecting" ? "badge-info" : "badge-error"
          }`}
        >
          {status === "connected" ? "Live" : status === "connecting" ? "Connecting…" : "Disconnected"}
        </span>
        <Link to={`/conversation/${ticket.dealerId}`} style={{ marginLeft: "auto", fontSize: 12 }}>
          View earlier &amp; later messages
        </Link>
      </div>
      {messages.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
          No recent messages on this conversation.
        </div>
      ) : (
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            maxHeight: 360,
            overflowY: "auto",
            padding: 12,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-sm)",
          }}
        >
          {messages.map((m) => (
            <MessageRow key={m.messageId} message={m} />
          ))}
        </div>
      )}
    </div>
  );
}

function TicketDetail({
  ticket,
  onAction,
}: {
  ticket: SupportTicket;
  onAction: () => void;
}) {
  const [responseText, setResponseText] = useState(
    ticket.suggestedResponses[0] ?? "",
  );
  const [assignTeam, setAssignTeam] = useState(ticket.assignedTeam ?? "customer_service");
  const [sending, setSending] = useState(false);
  const [unsentModalOpen, setUnsentModalOpen] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const user = getUserInfo();
  const dealer = dealerDisplay(ticket);
  const isClosed = ticket.status === "resolved" || ticket.status === "closed" || ticket.status === "closed_by_dealer";
  const hasUnsentResponse = responseText.trim().length > 0;

  function errorMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (typeof err === "string") return err;
    return "Unknown error";
  }

  async function sendResponseNow(): Promise<void> {
    await postSupportResponse({
      ticketId: ticket.ticketId,
      content: responseText,
      source: responseText === ticket.suggestedResponses[0] ? "suggested" : "edited",
      senderName: user?.givenName || undefined,
    });
    setResponseText("");
  }

  async function handleSendResponse() {
    if (!hasUnsentResponse) return;
    setSending(true);
    try {
      await sendResponseNow();
      onAction();
    } finally {
      setSending(false);
    }
  }

  async function handleAssign() {
    setSending(true);
    try {
      await postAssignTask({
        ticketId: ticket.ticketId,
        team: assignTeam,
        assignedTo: user?.email ?? "unassigned",
      });
      onAction();
    } finally {
      setSending(false);
    }
  }

  async function resolveNow(): Promise<void> {
    await postResolveTicket(ticket.ticketId);
  }

  async function handleResolveClick() {
    if (hasUnsentResponse) {
      setUnsentModalOpen(true);
      return;
    }
    const confirmed = window.confirm(
      "Resolve this ticket? This closes the support escalation and lets the bot reply to the dealer again.",
    );
    if (!confirmed) return;
    setSending(true);
    setResolveError(null);
    try {
      await resolveNow();
      onAction();
    } catch (err) {
      setResolveError(`Resolve failed: ${errorMessage(err)}`);
    } finally {
      setSending(false);
    }
  }

  async function handleModalSendAndResolve() {
    setSending(true);
    setResolveError(null);
    let sendOk = false;
    try {
      await sendResponseNow();
      sendOk = true;
    } catch (err) {
      alert(`Failed to send the response: ${errorMessage(err)}`);
      setSending(false);
      return;
    }
    setUnsentModalOpen(false);
    try {
      await resolveNow();
    } catch (err) {
      setResolveError(
        sendOk
          ? `Response sent to dealer, but resolving the ticket failed: ${errorMessage(err)}`
          : `Resolve failed: ${errorMessage(err)}`,
      );
    } finally {
      setSending(false);
      onAction();
    }
  }

  async function handleModalResolveOnly() {
    setSending(true);
    setResolveError(null);
    setResponseText("");
    setUnsentModalOpen(false);
    try {
      await resolveNow();
    } catch (err) {
      setResolveError(`Resolve failed: ${errorMessage(err)}`);
    } finally {
      setSending(false);
      onAction();
    }
  }

  async function handleRetryResolve() {
    setSending(true);
    setResolveError(null);
    try {
      await resolveNow();
      onAction();
    } catch (err) {
      setResolveError(`Resolve failed: ${errorMessage(err)}`);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <strong className="mono">{ticket.ticketId.slice(0, 8)}</strong>
        <span className={priorityBadge(ticket.priority)}>{ticket.priority}</span>
      </div>

      {resolveError && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: "8px 12px",
            background: "rgba(220, 74, 74, 0.12)",
            border: "1px solid rgba(220, 74, 74, 0.5)",
            borderRadius: 4,
            color: "var(--color-text)",
            fontSize: 13,
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <span>{resolveError}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              className="btn btn-sm"
              type="button"
              onClick={() => void handleRetryResolve()}
              disabled={sending}
            >
              Retry resolve
            </button>
            <button
              className="btn btn-sm"
              type="button"
              onClick={() => setResolveError(null)}
              disabled={sending}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <strong>Dealer:</strong>{" "}
        <Link to={`/dealer/${ticket.dealerId}`} className="dealer-link">
          {dealer.primary}
        </Link>
        {dealer.secondary && (
          <span style={{ color: "var(--color-text-secondary)" }}> · {dealer.secondary}</span>
        )}{" "}
        <span style={{ color: "var(--color-text-soft)", fontSize: 12 }}>
          #{ticket.dealerId}
        </span>
        <div style={{ marginTop: 4 }}>
          <strong>Reason:</strong> {ticket.escalationReason}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <LiveConversationContext ticket={ticket} />
      </div>

      {ticket.suggestedResponses.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div className="section-title">Suggested Responses</div>
          {ticket.suggestedResponses.map((sr, i) => (
            <div
              key={i}
              style={{
                padding: "6px 10px",
                background: "var(--color-surface-solid)",
                borderRadius: 4,
                marginBottom: 4,
                cursor: "pointer",
                border: responseText === sr ? "1px solid var(--color-accent)" : "1px solid var(--color-border)",
                color: "var(--color-text)",
                fontSize: 13,
              }}
              onClick={() => setResponseText(sr)}
            >
              {sr}
            </div>
          ))}
        </div>
      )}

      {ticket.suggestedActions.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div className="section-title">Suggested Actions</div>
          <ul style={{ paddingLeft: 16 }}>
            {ticket.suggestedActions.map((action, i) => (
              <li key={i} style={{ fontSize: 13 }}>{action}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="form-group">
        <label>Response to dealer</label>
        <textarea
          value={responseText}
          onChange={(e) => setResponseText(e.target.value)}
        />
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {!isClosed && (
          <button
            className="btn btn-sm"
            type="button"
            onClick={() => void handleResolveClick()}
            disabled={sending}
            style={{
              background: "var(--color-accent)",
              color: "#072018",
              borderColor: "var(--color-accent)",
            }}
            title="Close this ticket and let the bot resume normal replies to the dealer"
          >
            Resolve ticket
          </button>
        )}

        <div style={{ flex: 1 }} />

        <select
          value={assignTeam}
          onChange={(e) => setAssignTeam(e.target.value)}
          style={{ width: "auto" }}
        >
          <option value="customer_service">Customer Service</option>
          <option value="bidding_support">Bidding Support</option>
          <option value="transport">Transport</option>
          <option value="general">General</option>
        </select>

        <button
          className="btn btn-sm"
          type="button"
          onClick={() => void handleAssign()}
          disabled={sending}
        >
          Assign
        </button>

        <button
          className="btn btn-primary btn-sm"
          type="button"
          onClick={() => void handleSendResponse()}
          disabled={sending || !responseText.trim()}
        >
          Send Response
        </button>
      </div>

      {unsentModalOpen && (
        <UnsentResponseModal
          responsePreview={responseText}
          sending={sending}
          onSendAndResolve={() => void handleModalSendAndResolve()}
          onResolveWithoutSending={() => void handleModalResolveOnly()}
          onCancel={() => setUnsentModalOpen(false)}
        />
      )}
    </div>
  );
}

function UnsentResponseModal({
  responsePreview,
  sending,
  onSendAndResolve,
  onResolveWithoutSending,
  onCancel,
}: {
  responsePreview: string;
  sending: boolean;
  onSendAndResolve: () => void;
  onResolveWithoutSending: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--color-surface-solid)",
          border: "1px solid var(--color-border)",
          borderRadius: 8,
          maxWidth: 520,
          width: "90%",
          padding: 20,
          color: "var(--color-text)",
        }}
      >
        <h3 style={{ marginTop: 0 }}>Unsent response</h3>
        <p style={{ fontSize: 14, color: "var(--color-text-secondary)" }}>
          You have an unsent response to the dealer. Would you like to send it before resolving the ticket?
        </p>
        <div
          style={{
            padding: "8px 10px",
            marginBottom: 16,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: 4,
            fontSize: 13,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: 160,
            overflowY: "auto",
          }}
        >
          {responsePreview}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
          <button
            className="btn btn-sm"
            type="button"
            onClick={onCancel}
            disabled={sending}
          >
            Cancel
          </button>
          <button
            className="btn btn-sm"
            type="button"
            onClick={onResolveWithoutSending}
            disabled={sending}
          >
            Resolve without sending
          </button>
          <button
            className="btn btn-primary btn-sm"
            type="button"
            onClick={onSendAndResolve}
            disabled={sending}
          >
            Send &amp; resolve
          </button>
        </div>
      </div>
    </div>
  );
}

export function SupportQueuePage() {
  useDocumentTitle("Support Queue");
  const location = useLocation();
  const initialTicketRef = useRef(new URLSearchParams(location.search).get("ticket"));

  const [showResolved, setShowResolved] = useState<boolean>(
    () => readStoredString(STATUS_FILTER_STORAGE_KEY) === "true",
  );
  const [team, setTeam] = useState<string>(() => readStoredString(TEAM_FILTER_STORAGE_KEY));
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deepLinkTicketId, setDeepLinkTicketId] = useState<string | null>(
    () => initialTicketRef.current,
  );
  const [autoWidened, setAutoWidened] = useState(false);
  const [deepLinkNotFound, setDeepLinkNotFound] = useState<string | null>(null);

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [pagination, setPagination] = useState<SupportQueuePagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);

  const detailRef = useRef<HTMLDivElement | null>(null);
  const skipPersistRef = useRef(false);

  useEffect(() => {
    if (skipPersistRef.current) return;
    writeStoredString(STATUS_FILTER_STORAGE_KEY, showResolved ? "true" : "");
  }, [showResolved]);

  useEffect(() => {
    if (skipPersistRef.current) return;
    writeStoredString(TEAM_FILTER_STORAGE_KEY, team);
  }, [team]);

  useEffect(() => {
    setOffset(0);
    setExpandedId(null);
  }, [showResolved, team]);

  const params = useMemo<FetchSupportQueueParams>(
    () => ({
      status: showResolved ? "all" : "active",
      team: team || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [showResolved, team, offset],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSupportQueue(params)
      .then((res) => {
        if (cancelled) return;
        setTickets(res.tickets);
        setPagination(res.pagination);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load tickets");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [params, refetchTick]);

  const refetch = useCallback(() => setRefetchTick((t) => t + 1), []);

  useEffect(() => {
    if (!deepLinkTicketId || loading) return;
    const inList = tickets.some((t) => t.ticketId === deepLinkTicketId);
    if (inList) {
      setExpandedId(deepLinkTicketId);
      setDeepLinkTicketId(null);
      setDeepLinkNotFound(null);
      return;
    }
    if (!autoWidened && (!showResolved || team)) {
      skipPersistRef.current = true;
      setShowResolved(true);
      setTeam("");
      setAutoWidened(true);
      setTimeout(() => {
        skipPersistRef.current = false;
      }, 0);
      return;
    }
    setDeepLinkNotFound(deepLinkTicketId);
    setDeepLinkTicketId(null);
    if (autoWidened) {
      skipPersistRef.current = true;
      setShowResolved(readStoredString(STATUS_FILTER_STORAGE_KEY) === "true");
      setTeam(readStoredString(TEAM_FILTER_STORAGE_KEY));
      setAutoWidened(false);
      setTimeout(() => {
        skipPersistRef.current = false;
      }, 0);
    }
  }, [deepLinkTicketId, loading, tickets, autoWidened, showResolved, team]);

  useEffect(() => {
    if (!expandedId) return;
    const el = detailRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [expandedId]);

  const clearAutoWiden = useCallback(() => {
    skipPersistRef.current = true;
    setShowResolved(readStoredString(STATUS_FILTER_STORAGE_KEY) === "true");
    setTeam(readStoredString(TEAM_FILTER_STORAGE_KEY));
    setAutoWidened(false);
    setExpandedId(null);
    setTimeout(() => {
      skipPersistRef.current = false;
    }, 0);
  }, []);

  const total = pagination?.total ?? 0;
  const pageStart = tickets.length === 0 ? 0 : offset + 1;
  const pageEnd = offset + tickets.length;
  const hasMore = pagination?.hasMore ?? false;

  return (
    <>
      <div className="card-header">
        <h1 className="page-title">Support Queue</h1>
        <button className="btn btn-sm" type="button" onClick={refetch}>
          Refresh
        </button>
      </div>

      {autoWidened && (
        <div
          role="status"
          style={{
            marginBottom: 12,
            padding: "8px 12px",
            background: "rgba(53, 156, 220, 0.12)",
            border: "1px solid rgba(53, 156, 220, 0.45)",
            borderRadius: 4,
            fontSize: 13,
            color: "var(--color-text)",
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <span>
            Filters temporarily widened to show the deep-linked ticket. Your saved filters have not changed.
          </span>
          <button className="btn btn-sm" type="button" onClick={clearAutoWiden}>
            Reset filters
          </button>
        </div>
      )}

      {deepLinkNotFound && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: "8px 12px",
            background: "rgba(220, 74, 74, 0.12)",
            border: "1px solid rgba(220, 74, 74, 0.5)",
            borderRadius: 4,
            fontSize: 13,
            color: "var(--color-text)",
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <span>
            Couldn't find ticket <code>{deepLinkNotFound.slice(0, 8)}</code>. It may have been deleted or the link is out of date.
          </span>
          <button className="btn btn-sm" type="button" onClick={() => setDeepLinkNotFound(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
          />
          Show resolved
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          Team:
          <select
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            style={{ width: "auto" }}
          >
            {TEAM_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        {pagination && (
          <div style={{ marginLeft: "auto", fontSize: 13, color: "var(--color-text-secondary)" }}>
            {total === 0 ? "0 tickets" : `${pageStart}–${pageEnd} of ${total}`}
          </div>
        )}
      </div>

      {loading && <div className="loading">Loading tickets...</div>}
      {error && <div className="error-msg">{error}</div>}

      {!loading && !error && tickets.length === 0 && (
        <div className="empty-state">No support tickets</div>
      )}

      {!loading && !error && tickets.length > 0 && (
        <>
          <table>
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Dealer</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Team</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => {
                const dealer = dealerDisplay(ticket);
                return (
                  <tr
                    key={ticket.ticketId}
                    className="clickable-row"
                    onClick={() =>
                      setExpandedId(
                        expandedId === ticket.ticketId ? null : ticket.ticketId,
                      )
                    }
                  >
                    <td className="mono">{ticket.ticketId.slice(0, 8)}</td>
                    <td>
                      <Link
                        to={`/dealer/${ticket.dealerId}`}
                        className="dealer-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {dealer.primary}
                      </Link>
                      {dealer.secondary && (
                        <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                          {dealer.secondary}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={statusBadge(ticket.status)}>
                        {statusLabel(ticket.status)}
                      </span>
                    </td>
                    <td>
                      <span className={priorityBadge(ticket.priority)}>
                        {ticket.priority}
                      </span>
                    </td>
                    <td>{ticket.assignedTeam ?? "—"}</td>
                    <td className="mono">
                      {new Date(ticket.createdAt).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {expandedId && tickets.find((t) => t.ticketId === expandedId) && (
            <div ref={detailRef} style={{ marginTop: 12, scrollMarginTop: 12 }}>
              <TicketDetail
                ticket={tickets.find((t) => t.ticketId === expandedId)!}
                onAction={refetch}
              />
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 12,
              marginTop: 16,
            }}
          >
            <button
              className="btn btn-sm"
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              ← Previous
            </button>
            <span style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
              Page {Math.floor(offset / PAGE_SIZE) + 1}
              {total > 0 ? ` of ${Math.max(1, Math.ceil(total / PAGE_SIZE))}` : ""}
            </span>
            <button
              className="btn btn-sm"
              type="button"
              disabled={!hasMore}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </>
  );
}
